import React from 'react'
import moment from 'moment'
import RecordSection from './_RecordSection'
import { View, Text } from '@react-pdf/renderer'
import _news2calc from '../../_news2calc'
import _pewsCalc, {
  isUnder18,
  pewsTempWarning,
  resolvePewsAgeBand,
} from '../../_pewsCalc'

const VitalSigns = ({ epcr }) => {
  const paediatric = isUnder18(
    epcr.demographics,
    epcr.vitals?.obs?.[0]?.time,
  )
  const Element = ({ label, value, flex, flexBasis, bgColor, bold }) => (
    <View
      style={{
        flexDirection: 'column',
        borderStyle: 'solid',
        borderColor: '#f2f2f2',
        fontSize: 10,
        flex,
        flexBasis,
      }}
    >
      <View
        style={{
          padding: 4,
          backgroundColor: '#f2f2f2',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Text style={{ fontWeight: bold && 'bold' }}>{label}</Text>
      </View>
      <View
        style={{
          padding: 4,
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: bgColor,
        }}
      >
        <Text>{value}</Text>
      </View>
    </View>
  )
  return (
    <RecordSection title="Vital Signs">
      {!paediatric && (
        <View
          style={{ flexDirection: 'row', gap: 5, fontSize: '10px', margin: 8 }}
        >
          <Text>SPO2 Scale (NEWS2):</Text>
          <Text
            style={{
              fontFamily: 'Helvetica-Bold',
            }}
          >
            {epcr.pmhx.spo2Scale2 ? '2' : '1'}
          </Text>
        </View>
      )}

      <View style={{ flexDirection: 'column', gap: 10 }}>
        {epcr.vitals.obs.map((obs, index) => {
          const totalGCS =
            parseInt(obs.gcsEyes) +
            parseInt(obs.gcsVerbal) +
            parseInt(obs.gcsMotor)
          const score = paediatric
            ? _pewsCalc({
                obs,
                ageBand: resolvePewsAgeBand(epcr.demographics, obs),
              })
            : _news2calc({ obs, spo2Scale2: epcr.pmhx.spo2Scale2 })
          const tempWarning = paediatric && pewsTempWarning(obs.temp)
          return (
            <View
              key={index}
              style={{
                width: '100%',
                flexDirection: 'row',
                flexWrap: 'wrap',
                justifyContent: 'stretch',
                borderBottomStyle: 'solid',
                borderBottom: 1,
                borderTopStyle: 'solid',
                borderColor: '#616161',
                rowGap: 6,
              }}
              wrap={false}
            >
              <Element
                label="Time"
                value={moment(obs.time).format('HH:mm')}
                flex={1}
                flexBasis="60px"
                bold
              />
              <Element
                label="AVPU"
                value={obs.avpu}
                width={200}
                flex={1}
                flexBasis="120px"
              />
              <Element
                label="RR"
                value={`${obs.respRate && obs.respRate + ' bpm'}`}
                flex={1}
                flexBasis="70px"
              />
              <Element
                label="SPO2"
                value={`${obs.spo2 && obs.spo2 + '%'}`}
                flex={1}
                flexBasis="70px"
              />
              <Element
                label="HR"
                value={obs.heartRate}
                flex={1}
                flexBasis="70px"
              />
              <Element
                label="Temp"
                value={obs.temp + ' (°c)'}
                bgColor={tempWarning || undefined}
                flex={1}
                flexBasis="70px"
              />
              <Element
                label="GCS"
                value={
                  totalGCS >= 3 &&
                  `${totalGCS} (E${obs.gcsEyes}/V${obs.gcsVerbal}/M${obs.gcsMotor})`
                }
                flex={1}
                flexBasis="90px"
              />
              <Element
                label="BP"
                value={
                  obs.bpSys || obs.bpDia
                    ? `${obs.bpSys}/${obs.bpDia}` +
                      (obs.bpPosition ? ` (${obs.bpPosition})` : '')
                    : obs.bpDerogation || ''
                }
                flex={1}
                flexBasis="80px"
              />
              <Element
                label="Pain Score"
                value={obs.painScore}
                flex={1}
                flexBasis="60px"
              />
              <Element
                label="Supp. O2"
                value={
                  obs.suppO2 === 'Yes' &&
                  (obs.suppO2Fio2 ||
                    (obs.respSupport && obs.respSupport !== 'None'))
                    ? `Yes (${[
                        obs.suppO2Fio2 && `${obs.suppO2Fio2}%`,
                        obs.respSupport !== 'None' && obs.respSupport,
                      ]
                        .filter(Boolean)
                        .join(', ')})`
                    : obs.suppO2
                }
                flex={1}
                flexBasis="60px"
              />
              {paediatric && (
                <Element
                  label="Resp. Distress"
                  value={obs.respDistress}
                  flex={1}
                  flexBasis="60px"
                />
              )}
              <Element
                label="PEFR"
                value={`${obs.pefr}${obs.pefr + ' (l/min)'}`}
                flex={1}
                flexBasis="60px"
              />
              <Element
                label="ETCO2"
                value={obs.etco2}
                flex={1}
                flexBasis="60px"
              />
              <Element
                label="BGL"
                value={`${obs.bgl}${obs.bgl && ' (mmol/L)'}`}
                flex={1}
                flexBasis="60px"
              />
              <Element
                label={paediatric ? 'PEWS' : 'NEWS2'}
                value={score ? score.score : ''}
                bgColor={score.color}
                flex={1}
                flexBasis="60px"
              />
            </View>
          )
        })}
      </View>
    </RecordSection>
  )
}

export default VitalSigns
